import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Toast / snackbar primitive.
 *
 * Why this exists:
 *   The audit (docs/UI_UX_AUDIT.md §3, §6 P0-7) called out that every page
 *   was rebuilding "rose alert at top of card" for errors and had no
 *   convention at all for success messages. This module is the single
 *   source of truth for transient confirmation + error feedback.
 *
 * Public API:
 *   - <ToastProvider> ........... mount once near the root (already wired in main.tsx)
 *   - useToast() ................ returns { success, error, info, dismiss } imperative helpers
 *
 * Behaviour:
 *   - Stacks bottom-right; newest on top.
 *   - Auto-dismisses after 4 s for success/info, 6 s for error.
 *   - Click anywhere on the toast (or the X button) to dismiss early.
 *   - role="status" + aria-live="polite" so screen readers announce content
 *     without stealing focus.
 *   - Each toast has a fixed id; calling success/error twice with the same
 *     content does not collapse them — agents need to see repeats.
 */
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
const ToastCtx = createContext(null);
const AUTO_DISMISS_MS = {
    success: 4000,
    info: 4000,
    error: 6000, // give agents a beat longer to read failure detail
};
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    // Monotonic id seed; refs avoid re-render churn that useState would cause.
    const nextId = useRef(1);
    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    const push = useCallback((variant, title, description) => {
        const id = nextId.current++;
        setToasts((prev) => [...prev, { id, variant, title, description }]);
        window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS[variant]);
    }, [dismiss]);
    const api = useMemo(() => ({
        success: (title, description) => push('success', title, description),
        error: (title, description) => push('error', title, description),
        info: (title, description) => push('info', title, description),
        dismiss,
    }), [push, dismiss]);
    return (_jsxs(ToastCtx.Provider, { value: api, children: [children, _jsx(ToastViewport, { toasts: toasts, dismiss: dismiss })] }));
}
export function useToast() {
    const ctx = useContext(ToastCtx);
    if (!ctx)
        throw new Error('useToast must be used inside <ToastProvider>');
    return ctx;
}
// ---- Viewport -------------------------------------------------------------
function ToastViewport({ toasts, dismiss, }) {
    if (toasts.length === 0)
        return null;
    return (_jsx("div", { 
        // Fixed in the bottom-right; pointer-events-none so it doesn't block
        // the page underneath. Each toast turns events back on.
        className: "pointer-events-none fixed inset-x-0 bottom-4 z-[200] flex flex-col items-end gap-2 px-4 sm:bottom-6 sm:px-6", "aria-live": "polite", "aria-atomic": "false", children: toasts.map((t) => (_jsx(ToastItem, { toast: t, onDismiss: () => dismiss(t.id) }, t.id))) }));
}
function ToastItem({ toast, onDismiss, }) {
    const [enter, setEnter] = useState(false);
    useEffect(() => {
        // One-frame delay to trigger the slide-in transition.
        const id = window.requestAnimationFrame(() => setEnter(true));
        return () => window.cancelAnimationFrame(id);
    }, []);
    const palette = toast.variant === 'success'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-100'
        : toast.variant === 'error'
            ? 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/60 dark:text-rose-100'
            : 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/60 dark:text-sky-100';
    const Icon = toast.variant === 'success' ? CheckCircle2
        : toast.variant === 'error' ? AlertCircle
            : Info;
    return (_jsxs("div", { role: toast.variant === 'error' ? 'alert' : 'status', className: [
            'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border px-4 py-3 shadow-lg backdrop-blur-sm transition-all duration-200',
            palette,
            enter ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        ].join(' '), children: [_jsx(Icon, { className: "mt-0.5 h-5 w-5 shrink-0", "aria-hidden": "true" }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: "text-sm font-semibold", children: toast.title }), toast.description && (_jsx("div", { className: "mt-0.5 break-words text-xs opacity-90", children: toast.description }))] }), _jsx("button", { type: "button", onClick: onDismiss, className: "rounded p-0.5 opacity-70 transition hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current", "aria-label": "Dismiss notification", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) })] }));
}
